import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  Stack,
  Autocomplete,
  TextField,
  SelectChangeEvent,
  OutlinedInput
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { FiltrosRelatorio } from '../../services/relatorioService';
import departamentoService from '../../services/departamentoService';
import api from '../../services/api';

interface FiltrosRelatorioProps {
  onFiltrosChange: (filtros: FiltrosRelatorio) => void;
  tipoRelatorio: 'admin' | 'lider' | 'usuario';
  departamentoId?: string; // Para líder/usuário
  onExportar?: () => void; // Callback para exportação filtrada
  onExportarGeral?: () => void; // Callback para exportação geral (sem filtros)
  mostrarBotaoExportar?: boolean; // Controla visibilidade dos botões exportar
  filtrosAplicados?: FiltrosRelatorio; // Filtros que foram aplicados (para habilitar botão exportar)
}

interface Departamento {
  id: string;
  nome: string;
}

interface Usuario {
  id: string;
  nome: string;
  departamento?: string;
}

// Status agrupados para o frontend (sem duplicatas visuais)
// O backend já trata os sinônimos automaticamente
const STATUS_OPCOES = [
  { valor: '', label: 'Todos' },
  { valor: 'Pendente', label: 'Pendente' },
  { valor: 'A iniciar', label: 'A iniciar' },
  { valor: 'Em Andamento', label: 'Em Andamento' },
  { valor: 'Concluída', label: 'Concluída' },
  { valor: 'concluida_com_atraso', label: 'Concluída com Atraso' },
  { valor: 'arquivada', label: 'Arquivadas' }
];

const PRIORIDADE_OPCOES = [
  { valor: '', label: 'Todos' },
  { valor: 'baixa', label: 'Baixa' },
  { valor: 'media', label: 'Média' },
  { valor: 'alta', label: 'Alta' },
  { valor: 'urgente', label: 'Urgente' }
];

const FiltrosRelatorioComponent: React.FC<FiltrosRelatorioProps> = ({
  onFiltrosChange,
  tipoRelatorio,
  departamentoId,
  onExportar,
  onExportarGeral,
  mostrarBotaoExportar = false,
  filtrosAplicados
}) => {
  const [filtros, setFiltros] = useState<FiltrosRelatorio>({});
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [renderKey, setRenderKey] = useState(0);

  // Carregar departamentos (só para admin)
  useEffect(() => {
    if (tipoRelatorio === 'admin') {
      carregarDepartamentos();
    }
  }, [tipoRelatorio]);

  // Carregar usuários
  useEffect(() => {
    if (tipoRelatorio === 'admin' || tipoRelatorio === 'lider') {
      carregarUsuarios();
    }
  }, [tipoRelatorio, departamentoId]);

  const carregarDepartamentos = async () => {
    try {
      const response = await departamentoService.obterTodos();
      if (response.success && response.departamentos) {
        setDepartamentos(response.departamentos);
      } else {
        setDepartamentos([]);
      }
    } catch (error) {
      console.error('Erro ao carregar departamentos:', error);
      setDepartamentos([]);
    }
  };

  const carregarUsuarios = async () => {
    try {
      const response = await api.get('/usuarios');
      
      // O backend retorna { success: true, usuarios: [...] }
      const usuariosData = response.data.usuarios || response.data || [];
      const usuariosArray = Array.isArray(usuariosData) ? usuariosData : [];
      let usuariosFiltrados = usuariosArray.filter((u: any) => u.id && u.nome);
      
      // Aplicar filtro baseado no tipo de usuário
      if (tipoRelatorio === 'lider' && departamentoId) {
        // Líderes veem apenas usuários do seu departamento
        usuariosFiltrados = usuariosFiltrados.filter((u: any) => 
          u.departamento_id === departamentoId || u.departamentoId === departamentoId
        );
      } else if (tipoRelatorio === 'usuario') {
        // Usuários comuns veem apenas a si mesmos
        const usuarioAtual = localStorage.getItem('userId');
        usuariosFiltrados = usuariosFiltrados.filter((u: any) => u.id === usuarioAtual);
      }
      // Admin vê todos os usuários (sem filtro)
      
      // Ordenar alfabeticamente por nome
      const usuariosOrdenados = usuariosFiltrados.sort((a: any, b: any) => {
        return a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' });
      });
      
      setUsuarios(usuariosOrdenados);
    } catch (error) {
      setUsuarios([]);
    }
  };

  const handleDepartamentosChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFiltros({
      ...filtros,
      departamentos: typeof value === 'string' ? value.split(',') : value
    });
    // NÃO aplica automaticamente, espera o botão Buscar
  };

  const handleStatusChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const statusArray = typeof value === 'string' ? value.split(',') : value;
    
    setFiltros({
      ...filtros,
      status: statusArray
    });
    // NÃO aplica automaticamente, espera o botão Buscar
  };

  const handlePrioridadesChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFiltros({
      ...filtros,
      prioridades: typeof value === 'string' ? value.split(',') : value
    });
    // NÃO aplica automaticamente, espera o botão Buscar
  };

  const handleUsuariosChange = (event: any, newValue: Usuario[]) => {
    setFiltros({
      ...filtros,
      usuarios: newValue.map(u => u.id)
    });
    // NÃO aplica automaticamente, espera o botão Buscar
  };

  const handleDataInicioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFiltros({
      ...filtros,
      dataInicio: event.target.value
    });
    // NÃO aplica automaticamente, espera o botão Buscar
  };

  const handleDataFimChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFiltros({
      ...filtros,
      dataFim: event.target.value
    });
    // NÃO aplica automaticamente, espera o botão Buscar
  };

  const handleBuscar = () => {
    // Expandir status agrupados antes de enviar e remover valores vazios
    // Se status contém apenas string vazia ou está vazio, não enviar filtro de status
    const statusFiltrado = filtros.status?.filter(s => s !== '').flatMap(s => s.split(','));
    
    // Limpar valores undefined/null/vazios dos filtros
    const filtrosParaEnviar: FiltrosRelatorio = {};
    
    if (filtros.dataInicio) filtrosParaEnviar.dataInicio = filtros.dataInicio;
    if (filtros.dataFim) filtrosParaEnviar.dataFim = filtros.dataFim;
    if (filtros.departamentos && filtros.departamentos.length > 0) {
      filtrosParaEnviar.departamentos = filtros.departamentos;
    }
    if (statusFiltrado && statusFiltrado.length > 0) {
      filtrosParaEnviar.status = statusFiltrado;
    }
    const prioridadesFiltradas = filtros.prioridades?.filter(p => p !== '');
    if (prioridadesFiltradas && prioridadesFiltradas.length > 0) {
      filtrosParaEnviar.prioridades = prioridadesFiltradas;
    }
    if (filtros.usuarios && filtros.usuarios.length > 0) {
      filtrosParaEnviar.usuarios = filtros.usuarios;
    }
    
    // Forçar re-renderização dos campos Select
    setRenderKey(prev => prev + 1);
    
    onFiltrosChange(filtrosParaEnviar);
  };

  const limparFiltros = () => {
    const filtrosLimpos: FiltrosRelatorio = {};
    setFiltros(filtrosLimpos);
    onFiltrosChange(filtrosLimpos);
  };

  const temFiltrosAtivos = () => {
    // Usar filtrosAplicados se disponível, senão usar filtros locais
    const filtrosParaVerificar = filtrosAplicados || filtros;
    const statusValidos = filtrosParaVerificar.status?.filter(s => s !== '') || [];
    return (
      (filtrosParaVerificar.departamentos && filtrosParaVerificar.departamentos.length > 0) ||
      (statusValidos.length > 0) ||
      (filtrosParaVerificar.prioridades && filtrosParaVerificar.prioridades.length > 0) ||
      (filtrosParaVerificar.usuarios && filtrosParaVerificar.usuarios.length > 0) ||
      filtrosParaVerificar.dataInicio ||
      filtrosParaVerificar.dataFim
    );
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FilterListIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Filtros Avançados</Typography>
            {temFiltrosAtivos() && (
              <Chip 
                label="Filtros ativos" 
                size="small" 
                color="primary" 
                sx={{ ml: 2 }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {mostrarBotaoExportar && onExportarGeral && (
              <Button
                startIcon={<DownloadIcon />}
                onClick={onExportarGeral}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: '#4caf50',
                  color: '#4caf50',
                  '&:hover': {
                    borderColor: '#45a049',
                    backgroundColor: 'rgba(76, 175, 80, 0.04)'
                  }
                }}
              >
                Exportar Geral
              </Button>
            )}
            {mostrarBotaoExportar && onExportar && (
              <Button
                startIcon={<DownloadIcon />}
                onClick={onExportar}
                size="small"
                variant="contained"
                disabled={!temFiltrosAtivos()}
                sx={{
                  backgroundColor: '#4caf50',
                  '&:hover': {
                    backgroundColor: '#45a049'
                  }
                }}
              >
                Exportar Filtrado
              </Button>
            )}
            <Button
              startIcon={<SearchIcon />}
              onClick={handleBuscar}
              size="small"
              variant="contained"
              color="primary"
            >
              Buscar
            </Button>
            {temFiltrosAtivos() && (
              <Button
                startIcon={<ClearIcon />}
                onClick={limparFiltros}
                size="small"
                color="secondary"
              >
                Limpar
              </Button>
            )}
          </Box>
        </Box>

        <Stack spacing={2}>
          {/* Linha 1: Datas */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Data Início"
              type="date"
              value={filtros.dataInicio || ''}
              onChange={handleDataInicioChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="Data Fim"
              type="date"
              value={filtros.dataFim || ''}
              onChange={handleDataFimChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Stack>

          {/* Linha 2: Departamentos, Status e Prioridades */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {/* Filtro de Departamento (só para admin) */}
            {tipoRelatorio === 'admin' && (
              <FormControl fullWidth size="small">
                <InputLabel>Departamentos</InputLabel>
                <Select
                  key={`dept-${renderKey}`}
                  multiple
                  value={filtros.departamentos || []}
                  onChange={handleDepartamentosChange}
                  input={<OutlinedInput label="Departamentos" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const dept = departamentos.find(d => d.id === value);
                        return (
                          <Chip key={value} label={dept?.nome || value} size="small" />
                        );
                      })}
                    </Box>
                  )}
                >
                  {departamentos.length === 0 ? (
                    <MenuItem disabled>Carregando...</MenuItem>
                  ) : (
                    departamentos.map((dept) => (
                      <MenuItem key={dept.id} value={dept.id}>
                        {dept.nome}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )}

            {/* Filtro de Status */}
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                key={`status-${renderKey}`}
                multiple
                value={filtros.status || []}
                onChange={handleStatusChange}
                input={<OutlinedInput label="Status" />}
                renderValue={(selected) => {
                  // Se nenhum status selecionado ou 'Todos' selecionado, mostrar 'Todos'
                  if (selected.length === 0 || selected.includes('')) {
                    return <Chip label="Todos" size="small" />;
                  }
                  // Mostrar labels dos status selecionados
                  const labels = selected.map(valor => {
                    const opcao = STATUS_OPCOES.find(opt => opt.valor === valor);
                    return opcao ? opcao.label : valor;
                  });
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {labels.map((label) => (
                        <Chip key={label} label={label} size="small" />
                      ))}
                    </Box>
                  );
                }}
              >
                {STATUS_OPCOES.map((status) => (
                  <MenuItem key={status.valor} value={status.valor}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Filtro de Prioridade */}
            <FormControl fullWidth size="small">
              <InputLabel>Prioridades</InputLabel>
              <Select
                key={`prior-${renderKey}`}
                multiple
                value={filtros.prioridades || []}
                onChange={handlePrioridadesChange}
                input={<OutlinedInput label="Prioridades" />}
                renderValue={(selected) => {
                  if (selected.length === 0 || selected.includes('')) {
                    return <Chip label="Todos" size="small" />;
                  }
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const prior = PRIORIDADE_OPCOES.find(p => p.valor === value);
                        return (
                          <Chip key={value} label={prior?.label || value} size="small" />
                        );
                      })}
                    </Box>
                  );
                }}
              >
                {PRIORIDADE_OPCOES.map((prioridade) => (
                  <MenuItem key={`prior-opt-${prioridade.valor}`} value={prioridade.valor}>
                    {prioridade.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Linha 3: Filtro de Colaboradores — oculto no relatório pessoal */}
          {tipoRelatorio !== 'usuario' && (
            <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
              <Autocomplete
                multiple
                options={usuarios}
                getOptionLabel={(option) => option.nome}
                value={usuarios.filter(u => filtros.usuarios?.includes(u.id))}
                onChange={handleUsuariosChange}
                loading={usuarios.length === 0}
                loadingText="Carregando..."
                noOptionsText="Nenhum usuário encontrado"
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Colaboradores" 
                    size="small"
                    placeholder={usuarios.length === 0 ? "Carregando..." : "Selecione colaboradores"}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip 
                      label={option.nome} 
                      {...getTagProps({ index })} 
                      size="small" 
                    />
                  ))
                }
              />
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default FiltrosRelatorioComponent;
